import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, troubleSleeveRows, TROUBLE_CATEGORY_MISSING, TROUBLE_WRONG_HOUSEHOLD,
} from '../GuideKit';

/* Trading → Trade Error / Trade Review → Trade Error / Trade Review.
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Trading
 * (version active 2026-10-04): screen "Trade Error / Trade Review" (shared by
 * both sub-categories), then "Trade Error" or "Trade Review". The two
 * sub-categories ask the same questions; only the account table's label and
 * prompt differ. Shared chrome and policy copy live in GuideKit.js.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Trading',
    category: 'Trade Error / Trade Review',
    subCategory: 'Trade Error or Trade Review',
    subject: subjectRow('Trade error - 0989-022356'),
    lookups: [['Household', 'The client household. It decides which accounts you can pick later, and the advisor and office are taken from it.']],
    note: (
      <>
        <strong>Trade Error and Trade Review ask exactly the same questions.</strong> Choose Trade Error when
        something was traded wrongly, and Trade Review when you want Trading to look at an account&rsquo;s trading.
      </>
    ),
  }),
  {
    title: 'Answer the Trade Error / Trade Review questions',
    body: (
      <p style={{ margin: 0 }}>
        Three questions appear one at a time: the date, an earlier case or ticket number, then a details box.
        Each appears once the one above it has an answer, and all three are required. The details box becomes
        the case description Trading reads first.
      </p>
    ),
  },
  {
    title: 'Choose the accounts involved',
    body: (
      <p style={{ margin: 0 }}>
        The next screen (<strong>Trade Error</strong> or <strong>Trade Review</strong>) shows one table of the
        household&rsquo;s accounts with Account Number, Account Details, Model, Cash Balance and Balance. Tick every
        account involved - you can pick more than one.
      </p>
    ),
  },
  {
    title: 'Acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        Read the Operations and Trading notice under the table - it governs timing and what the teams can and
        cannot do. Select <em>I have read, understand, and agree to the above information</em> under
        Acknowledgement and click Next. FSC creates the case and opens it. Note the case number for follow-up.
      </p>
    ),
  },
];

const QUESTIONS = [
  {
    screen: 'Trade Error / Trade Review',
    q: 'When did the error occur?',
    when: 'Always - this is the first question, for both sub-categories',
    note: 'A date. It is saved on the case as the event date. For a Trade Review, give the date of the trading you want reviewed.',
  },
  {
    q: 'Previous Case / Ticket Number (If applicable)',
    when: 'Once a date is entered',
    note: 'The form will not let you continue with this blank. If there is no earlier case or ticket, type "None".',
  },
  {
    q: 'Please provide as much information as possible for the Trading Team...',
    when: 'Once the case / ticket number box has an answer',
    note: 'This becomes the case description. Say what was expected, what actually happened, the securities and amounts involved, and when the client noticed.',
  },
  {
    screen: 'Trade Error or Trade Review',
    q: 'Select Accounts (Trade Error) · Select Accounts (Trade Review)',
    when: 'Second screen - the label matches the sub-category you chose',
    note: 'Trade Error prompts "Please select the accounts involved in the Trade Error below"; Trade Review prompts "Please select the accounts that you need the Trading Team to review from the list below." Tick as many rows as apply.',
  },
  {
    q: 'Acknowledgement',
    when: 'Second screen, under the notice',
    options: ['I have read, understand, and agree to the above information'],
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Trading', 'Trade Error / Trade Review'),
  {
    h: "The next question hasn't appeared",
    p: ['Each question on the first screen appears only once the one above it has an answer. Enter the date first, then the case / ticket number.'],
  },
  troubleSleeveRows('This table is not filtered - every sleeve under the household is listed, including inactive and cancelled (CNX) sleeves, so check the account number before you tick it.'),
  TROUBLE_WRONG_HOUSEHOLD,
];

export default function TradeErrorReview() {
  return (
    <GuidePage
      title="Trade Error and Trade Review Requests"
      icon="exclamation-octagon"
      path="Trading → Trade Error / Trade Review → Trade Error or Trade Review"
      minutes={3}
      intro="How to report a trade error, or ask the Prosperity Trading team to review an account's trading - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready.</Para>
        <Bullets items={[
          <><strong>The date</strong> the error occurred, or of the trading you want reviewed.</>,
          <><strong>Any earlier case or ticket number</strong> about the same issue.</>,
          <><strong>What happened</strong> - what was expected, what was actually traded, securities and amounts.</>,
          <><strong>The accounts involved</strong>, all under the same household. You pick them from a list, so you do not need account numbers memorised.</>,
        ]} />
        <Callout>
          The account table only shows accounts under the household you choose on the header, so check which
          household the affected accounts sit under before you start.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to prepare, or to work out why a question you expected has not shown up.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext team="trading" />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It does not describe how Trading investigates or
        corrects an error, or who bears its cost.
      </Footnote>
    </GuidePage>
  );
}
